// Dial-code list for the contact-number country selector.
// Derived once from country-state-city (already a project dependency) so we
// don't hand-maintain a country table. Each entry: { iso, name, flag, code }.
//
// De-dupe: many countries share a dial code (e.g. +1 for US, Canada, Caribbean
// nations). We keep every country as its own <option> (unique iso key), but a
// small PRIORITY list is sorted to the top so the "representative" option shown
// for a shared code is the expected one (+1 → United States, +44 → United
// Kingdom, +91 → India) rather than whichever country happens to sort first.
import { Country } from 'country-state-city';

const PRIORITY = ['US', 'GB', 'IN', 'CA', 'AU', 'AE', 'DE', 'FR', 'SG', 'NL', 'ES', 'IT', 'JP', 'CN', 'BR', 'ZA', 'SA', 'MX'];

// Normalise a raw phonecode from the library into a "+NN" string. Values are
// almost always bare digits ("91"), sometimes already prefixed ("+91"); we
// strip anything non-digit and re-add the plus.
function toDial(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/[^0-9]/g, '');
  return digits ? `+${digits}` : '';
}

export const DIAL_CODES = (() => {
  const all = (Country.getAllCountries() || [])
    .map((c) => ({ iso: c.isoCode, name: c.name, flag: c.flag || '', code: toDial(c.phonecode) }))
    .filter((c) => c.code);

  const rank = (iso) => {
    const i = PRIORITY.indexOf(iso);
    return i === -1 ? PRIORITY.length : i;
  };

  return all.sort((a, b) => {
    const ra = rank(a.iso);
    const rb = rank(b.iso);
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name);
  });
})();

// Set of all valid "+NN" codes, so parsing can tell a real code from a bare
// number that merely starts with "+".
export const DIAL_CODE_SET = new Set(DIAL_CODES.map((c) => c.code));

// Split a stored contact value into { code, number }. Our write format is
// always `"+CC <number>"` (a space after the code), so a leading "+digits "
// with a code we recognise is treated as the country code; anything else
// (legacy raw numbers, "+91-98..." with no space) is left whole as the number
// with no code selected — non-destructive.
export function parsePhone(value) {
  if (!value || typeof value !== 'string') return { code: '', number: value || '' };
  const m = value.match(/^(\+\d{1,5})\s+([\s\S]*)$/);
  if (m && DIAL_CODE_SET.has(m[1])) {
    return { code: m[1], number: m[2] };
  }
  return { code: '', number: value };
}

// Recombine a code + number into the stored string. Empty code → just the
// number (so we never write a dangling "+91 " with no number, and legacy
// numbers stay untouched until a code is picked).
export function combinePhone(code, number) {
  const n = (number || '').trim();
  if (!code) return n;
  if (!n) return '';
  return `${code} ${n}`;
}
